/** @jsxImportSource @emotion/react */
import { type SerializedStyles, css as emotionCss } from '@emotion/react';
import { useState } from 'react';
import Collapse from '../../UI/Collapse';
import { additionalFAQ, defaultFAQ } from './defaultFAQ';
import {
  additionalCollapseStyle,
  additionalGroupStyle,
  bodyColStyle,
  borderBottomStyle,
  collapseStyle,
  collapseWrapper,
  faqWrapper,
  groupWrapper,
  headerWrapper,
  readMoreWrapper,
} from './style';
// @ts-ignore
import { useProducts } from '@shared/react-product';
import { Caret } from '../../Icons';
import { Button, SmoothMount } from '../../UI';

const FAQ: React.FC<
  React.PropsWithChildren & {
    questions?: { heading: string; details: string | React.ReactElement }[];
    showAdditional?: boolean;
    borderBottom?: boolean;
    collapse?: boolean;
    css?: SerializedStyles;
    wide?: boolean;
  }
> = ({
  questions = defaultFAQ,
  showAdditional = false,
  borderBottom = true,
  collapse = false,
  css,
  wide = false,
  ...props
}) => {
  const [showCollapse, setShowCollapse] = useState(!collapse);

  return (
    <section
      css={emotionCss`
        ${faqWrapper}
        ${borderBottom ? borderBottomStyle : ''}
        ${wide ? 'max-width: 1167px;' : 'max-width: 720px;'}
        ${css}
      `}
      {...props}
    >
      <div
        css={headerWrapper}
        style={{
          marginBottom: showCollapse ? 35 : 0,
          transition: 'all .3s ease',
        }}
      >
        <h4 className="text-h3">Frequently Asked Questions</h4>
        {collapse && (
          <Button
            cypressId="faq-collapse-button"
            onClick={() => setShowCollapse(!showCollapse)}
            style="text"
            color="purple"
          >
            <span>View</span>{' '}
            <Caret
              large
              css={emotionCss`
            transform: rotate(${showCollapse ? '180deg' : 0});
            transition: all .3s ease;
          `}
            />
          </Button>
        )}
      </div>
      <SmoothMount show={showCollapse}>
        <div css={collapseWrapper} id="faq-wrapper">
          <Collapse items={questions} css={collapseStyle} id="faq-collapse" />
        </div>
        {showAdditional && (
          <Collapse
            id="additional-faq"
            css={additionalCollapseStyle}
            icon={<></>}
            items={[
              {
                heading: 'Additional FAQs',
                details: (
                  <>
                    <Collapse
                      id="additional-faq-wrapper"
                      items={additionalFAQ.map(
                        (
                          g: {
                            name: string;
                            questions: {
                              question: string;
                              answer: { col1: string; col2: string };
                            }[];
                          },
                          i
                        ) => ({
                          heading: (
                            <div css={additionalGroupStyle}>{g.name}</div>
                          ),
                          details: (
                            <>
                              <Group
                                key={`group-${i}`}
                                questions={g.questions}
                              />
                            </>
                          ),
                        })
                      )}
                    />
                  </>
                ),
              },
            ]}
          />
        )}
      </SmoothMount>
    </section>
  );
};

const Group: React.FC<{
  questions: { question: string; answer: { col1: string; col2: string } }[];
}> = ({ questions }) => {
  return (
    <div style={{ padding: '0 0 15px' }}>
      <Collapse
        id="additonal-group-collapse"
        icon={<></>}
        items={questions.map(
          (
            question: {
              question: string;
              answer: { col1: string; col2: string };
            },
            j: number
          ) => ({
            heading: (
              <div css={groupWrapper}>
                {question.question}
                <p css={readMoreWrapper}>Read More</p>
              </div>
            ),
            details: (
              <>
                <GroupQuestion key={`question-${j}`} answer={question.answer} />
              </>
            ),
          })
        )}
      />
    </div>
  );
};

const GroupQuestion: React.FC<{
  answer: { col1: string; col2: string };
}> = ({ answer }) => {
  const products = useProducts();

  const replaceMonitoringPriceTokens = (str: string) => {
    if (!str) return '';
    let newString = str.replace(
      `{monitoring-price-bm-pn}`,
      `$${products?.['bm-pn']?.price}`
    );
    let result = newString.replace(
      `{monitoring-price-vm-pn}`,
      `$${products?.['vm-pn']?.price}`
    );
    return result;
  };
  return (
    <>
      <BodyCol text={replaceMonitoringPriceTokens(answer?.col1)} />
      <BodyCol text={replaceMonitoringPriceTokens(answer?.col2)} />
    </>
  );
};

const BodyCol: React.FC<{
  text: string;
}> = ({ text }) => {
  if (!text) return null;
  return (
    <div
      css={bodyColStyle}
      style={{ padding: '0 0 30px', fontWeight: '300' }}
      dangerouslySetInnerHTML={{
        __html: text.replace(/\n/g, '<br>'),
      }}
    />
  );
};

export default FAQ;
